import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { 
  Heart,
  DollarSign, 
  Utensils,
  Upload,
  Smile,
  UserPlus
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { useState } from "react";

const HowItWorks = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<'consumer' | 'creator'>('consumer');

  const handleCreateRecipe = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      toast("Please sign up to start creating recipes", {
        description: "Join our community of air fryer enthusiasts!"
      });
      navigate("/login?signup=true");
      return;
    }
    
    navigate("/create-recipe");
  };

  return (
    <>
      <Header />
      <main className="min-h-screen py-12 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              How Airfryer.AI Works
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              Whether you're here to cook amazing food or share your culinary expertise,
              we've got you covered.
            </p>
            
            <div className="flex justify-center gap-4 mb-12">
              <Button
                variant={activeSection === 'consumer' ? "default" : "outline"}
                onClick={() => setActiveSection('consumer')}
                className="min-w-[200px]"
              >
                For Food Lovers
              </Button>
              <Button
                variant={activeSection === 'creator' ? "default" : "outline"}
                onClick={() => setActiveSection('creator')}
                className="min-w-[200px]"
              >
                For Recipe Creators
              </Button>
            </div>
          </div>

          {activeSection === 'consumer' ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="h-12 w-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Utensils className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Cook Great Food</h3>
                <p className="text-gray-600 mb-4">
                  Access a wide variety of air fryer recipes, perfectly converted and ready to cook.
                  No more guessing temperatures or times.
                </p>
                <ul className="space-y-2 text-sm text-gray-500">
                  <li className="flex items-center gap-2">
                    <Heart className="h-4 w-4" />
                    <span>Perfectly converted recipes</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Smile className="h-4 w-4" />
                    <span>Easy to follow instructions</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="h-12 w-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Heart className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Discover New Favorites</h3>
                <p className="text-gray-600 mb-4">
                  Explore recipes from creators worldwide, filtered by cuisine, nationality,
                  or food type. Find your next favorite dish.
                </p>
                <ul className="space-y-2 text-sm text-gray-500">
                  <li className="flex items-center gap-2">
                    <Utensils className="h-4 w-4" />
                    <span>Global cuisine variety</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Heart className="h-4 w-4" />
                    <span>Save your favorites</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="h-12 w-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Smile className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Join the Community</h3>
                <p className="text-gray-600 mb-4">
                  Connect with other food enthusiasts, share your cooking experiences,
                  and learn from others.
                </p>
                <ul className="space-y-2 text-sm text-gray-500">
                  <li className="flex items-center gap-2">
                    <UserPlus className="h-4 w-4" />
                    <span>Follow favorite creators</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Heart className="h-4 w-4" />
                    <span>Share your experiences</span>
                  </li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="h-12 w-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Upload className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Create & Convert</h3>
                <p className="text-gray-600 mb-4">
                  Convert any recipe for air fryer use. Upload via URL, text, or photos.
                  Our AI handles the conversion instantly.
                </p>
                <ul className="space-y-2 text-sm text-gray-500">
                  <li className="flex items-center gap-2">
                    <Upload className="h-4 w-4" />
                    <span>Multiple upload methods</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Utensils className="h-4 w-4" />
                    <span>Instant AI conversion</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="h-12 w-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <DollarSign className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Earn Money</h3>
                <p className="text-gray-600 mb-4">
                  Monetize your expertise by offering premium recipes. Build a subscriber
                  base and earn from your culinary knowledge.
                </p>
                <ul className="space-y-2 text-sm text-gray-500">
                  <li className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    <span>Set your own prices</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <UserPlus className="h-4 w-4" />
                    <span>Build your audience</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="h-12 w-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <UserPlus className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Grow Your Brand</h3>
                <p className="text-gray-600 mb-4">
                  Track your success with detailed analytics. Monitor views, subscriptions,
                  and earnings to optimize your content.
                </p>
                <ul className="space-y-2 text-sm text-gray-500">
                  <li className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    <span>Revenue tracking</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Heart className="h-4 w-4" />
                    <span>Engagement metrics</span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          <div className="text-center bg-primary/5 rounded-2xl p-8 md:p-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              {activeSection === 'consumer' 
                ? "Ready to Start Your Culinary Journey?" 
                : "Ready to Share Your Recipes?"}
            </h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              {activeSection === 'consumer'
                ? "Join thousands of food enthusiasts discovering amazing air fryer recipes"
                : "Join our community of creators and start building your culinary empire"}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {activeSection === 'consumer' ? (
                <>
                  <Button 
                    size="lg" 
                    onClick={() => navigate("/recipes")}
                    className="bg-primary hover:bg-primary/90 text-white"
                  >
                    Explore Recipes
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    onClick={() => setActiveSection('creator')}
                    className="border-primary text-primary hover:bg-primary/10"
                  >
                    Become a Creator
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    size="lg" 
                    onClick={handleCreateRecipe}
                    className="bg-primary hover:bg-primary/90 text-white"
                  >
                    Create Your First Recipe
                  </Button>
                  <Button 
                    size="lg" 
                    onClick={() => navigate("/creators")}
                    className="bg-primary hover:bg-primary/90 text-white"
                  >
                    Learn How to Earn
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    onClick={() => navigate("/recipes")}
                    className="border-primary text-primary hover:bg-primary/10"
                  >
                    Browse Recipes
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default HowItWorks;
